import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Users, Monitor, Smartphone, PieChart, BarChart3 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface ICPData {
    how_discovered: Record<string, number>;
    occupation: Record<string, number>;
    main_goal: Record<string, number>;
    totalResponses: number;
}

const LABELS: Record<string, Record<string, string>> = {
    how_discovered: {
        google: 'Google',
        instagram: 'Instagram',
        tiktok: 'TikTok',
        youtube: 'YouTube',
        friend: 'Indicação',
        ads: 'Anúncios',
        other: 'Outro',
    },
    occupation: {
        content_creator: 'Criador de Conteúdo',
        marketer: 'Marketing',
        business_owner: 'Empresário',
        freelancer: 'Freelancer',
        student: 'Estudante',
        other: 'Outro',
    },
    main_goal: {
        grow_followers: 'Crescer Seguidores',
        sell_products: 'Vender Produtos',
        brand_content: 'Conteúdo p/ Marcas',
        personal_brand: 'Marca Pessoal',
        other: 'Outro',
    },
};

const COLORS = [
    'bg-primary',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-orange-500',
];

export function ICPAnalytics() {
    const [data, setData] = useState<ICPData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadICPData();
    }, []);

    const loadICPData = async () => {
        try {
            // Fetch all profiles with ICP data
            const { data: profiles, error } = await supabase
                .from('profiles')
                .select('how_discovered, occupation, main_goal, onboarding_completed_at');
            // Note: We removed the filter .not('onboarding_completed_at', 'is', null)
            // to include manually created users who never completed onboarding

            if (error) throw error;

            // Aggregate data
            const howDiscovered: Record<string, number> = {};
            const occupation: Record<string, number> = {};
            const mainGoal: Record<string, number> = {};

            profiles?.forEach((profile: any) => {
                if (profile.how_discovered) {
                    howDiscovered[profile.how_discovered] = (howDiscovered[profile.how_discovered] || 0) + 1;
                }
                if (profile.occupation) {
                    occupation[profile.occupation] = (occupation[profile.occupation] || 0) + 1;
                }
                if (profile.main_goal) {
                    mainGoal[profile.main_goal] = (mainGoal[profile.main_goal] || 0) + 1;
                }
            });

            setData({
                how_discovered: howDiscovered,
                occupation: occupation,
                main_goal: mainGoal,
                totalResponses: profiles?.length || 0,
            });
        } catch (err) {
            console.error('Error loading ICP data:', err);
            setData({
                how_discovered: {},
                occupation: {},
                main_goal: {},
                totalResponses: 0,
            });
        } finally {
            setLoading(false);
        }
    };

    const renderBarChart = (
        data: Record<string, number>,
        labels: Record<string, string>,
        title: string
    ) => {
        const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
        const total = entries.reduce((sum, [_, count]) => sum + count, 0);

        if (entries.length === 0) {
            return (
                <div className="text-center py-4 text-muted-foreground">
                    Sem dados ainda
                </div>
            );
        }

        return (
            <div className="space-y-3">
                {entries.map(([key, count], index) => {
                    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                        <div key={key} className="space-y-1">
                            <div className="flex justify-between text-sm">
                                <span className="font-medium">{labels[key] || key}</span>
                                <span className="text-muted-foreground">
                                    {count} ({percentage}%)
                                </span>
                            </div>
                            <div className="h-2 bg-secondary rounded-full overflow-hidden">
                                <div
                                    className={`h-full ${COLORS[index % COLORS.length]} rounded-full transition-all duration-500`}
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <PieChart className="w-5 h-5" />
                        Perfil dos Usuários (ICP)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="space-y-3">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-2 w-full" />
                                <Skeleton className="h-2 w-3/4" />
                                <Skeleton className="h-2 w-1/2" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border-primary/20">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-primary" />
                        Perfil dos Usuários (ICP)
                    </CardTitle>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="w-4 h-4" />
                        {data?.totalResponses || 0} respostas
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Como Conheceram */}
                    <div>
                        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Como conheceram
                        </h4>
                        {renderBarChart(
                            data?.how_discovered || {},
                            LABELS.how_discovered,
                            'Como conheceram'
                        )}
                    </div>

                    {/* Ocupação */}
                    <div>
                        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            Ocupação
                        </h4>
                        {renderBarChart(
                            data?.occupation || {},
                            LABELS.occupation,
                            'Ocupação'
                        )}
                    </div>

                    {/* Objetivo */}
                    <div>
                        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Objetivo Principal
                        </h4>
                        {renderBarChart(
                            data?.main_goal || {},
                            LABELS.main_goal,
                            'Objetivo'
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
